import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Eye, Download, ArrowLeft, RefreshCw, Plus, Trash2 } from "lucide-react";
import { api, pdfUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import ImageUpload from "../components/ImageUpload";
import MarksheetStudio from "../components/MarksheetStudio";

function todayStr() {
  return new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const GRADE_SCALE_TEXT = "A+ Above 85% · A 75–84% · B+ 65–74% · B 50–64% · C Below 50%";

function gradeFor(pct) {
  if (pct >= 85) return "A+";
  if (pct >= 75) return "A";
  if (pct >= 65) return "B+";
  if (pct >= 50) return "B";
  return "C";
}

export default function NewCertificate() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [editIsMarksheet, setEditIsMarksheet] = useState(false);
  const msMode = searchParams.get("format") === "2" || editIsMarksheet;
  const [courses, setCourses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [nextNum, setNextNum] = useState("");
  const [form, setForm] = useState({
    format: 1,
    student_name: "",
    father_name: "",
    mother_name: "",
    mobile_number: "",
    course_id: "",
    course_name: "",
    course_duration: "",
    session: "",
    institute_name: "",
    subjects: [],
    marks: [],
    enrollment_number: "",
    registration_number: "",
    admission_number: "",
    certificate_number: "",
    issue_date: todayStr(),
    grade: "",
    photo: null,
    template_id: "",
  });
  const [selectedSubjectIds, setSelectedSubjectIds] = useState(new Set());
  const [preview, setPreview] = useState({ open: false, url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [cRes, tRes, sRes] = await Promise.all([
        api.get("/courses"), api.get("/templates"), api.get("/settings"),
      ]);
      setCourses(cRes.data);
      setTemplates(tRes.data);
      const def = tRes.data.find((x) => x.is_default) || tRes.data[0];

      if (sid) {
        const { data } = await api.get(`/certificates/${sid}`);
        setForm({ ...data, subjects: data.subjects || [], marks: data.marks || [] });
        setSelectedSubjectIds(new Set((data.subjects || []).map((s) => s.id)));
        if (Number(data.format) === 2) setEditIsMarksheet(true);
      } else {
        setForm((f) => ({
          ...f,
          template_id: def?.id || "",
          institute_name: sRes.data?.institute_name || "",
        }));
        const { data } = await api.get("/certificates/next-number");
        setNextNum(data.certificate_number);
        setForm((f) => ({
          ...f,
          certificate_number: data.certificate_number,
          registration_number: data.registration_number || f.registration_number,
        }));
      }
    })();
     
  }, [sid]);

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === form.course_id),
    [courses, form.course_id]
  );
  const term1 = selectedCourse?.subjects?.filter((s) => s.term === 1) || [];
  const term2 = selectedCourse?.subjects?.filter((s) => s.term === 2) || [];

  const prefillMarks = (course) =>
    (course?.subjects || []).map((s) => ({
      id: `${s.id}-${Date.now()}`,
      subject: s.name,
      total: 100,
      min: 35,
      obtained: "",
    }));

  const onCourseChange = (cid) => {
    const c = courses.find((x) => x.id === cid);
    if (!c) return;
    setSelectedSubjectIds(new Set(c.subjects.map((s) => s.id)));
    setForm((f) => ({
      ...f,
      course_id: c.id,
      course_name: c.name,
      course_duration: c.duration,
      subjects: c.subjects,
      marks: f.format === 2 ? prefillMarks(c) : f.marks,
    }));
  };

  const setFormat = (fmt) => {
    if (fmt === 2) {
      nav("/admin/new?format=2");
    } else {
      nav("/admin/new");
    }
  };

  const toggleSubject = (subj) => {
    const next = new Set(selectedSubjectIds);
    if (next.has(subj.id)) next.delete(subj.id);
    else next.add(subj.id);
    setSelectedSubjectIds(next);
    setForm((f) => ({
      ...f,
      subjects: (selectedCourse?.subjects || []).filter((s) => next.has(s.id)),
    }));
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updMark = (id, k, v) =>
    setForm((f) => ({ ...f, marks: f.marks.map((m) => (m.id === id ? { ...m, [k]: v } : m)) }));
  const addMark = () =>
    setForm((f) => ({
      ...f,
      marks: [...f.marks, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, subject: "", total: 100, min: 35, obtained: "" }],
    }));
  const delMark = (id) =>
    setForm((f) => ({ ...f, marks: f.marks.filter((m) => m.id !== id) }));

  const totals = useMemo(() => {
    const t = form.marks.reduce((a, m) => a + (parseFloat(m.total) || 0), 0);
    const o = form.marks.reduce((a, m) => a + (parseFloat(m.obtained) || 0), 0);
    const pct = t ? Math.round((o / t) * 10000) / 100 : 0;
    const failed = form.marks.some(
      (m) => m.obtained !== "" && (parseFloat(m.obtained) || 0) < (parseFloat(m.min) || 0)
    );
    return { t, o, pct, grade: gradeFor(pct), status: failed ? "FAIL" : "PASS" };
  }, [form.marks]);

  const buildPayload = () => {
    const p = { ...form };
    if (p.format === 2) {
      if (!p.grade) p.grade = totals.grade;
      p.total_marks = totals.t;
      p.marks_obtained = totals.o;
      p.percentage = totals.pct;
      p.result = totals.status;
    }
    return p;
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post(
        "/certificates/preview",
        { student: buildPayload(), template_id: form.template_id || null },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPreview({ open: true, url });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    }
  };

  const doSave = async (openAfter = true) => {
    if (!form.student_name || !form.course_id) {
      toast.error("Student name and course are required");
      return;
    }
    if (form.format === 2 && form.marks.length === 0) {
      toast.error("Add at least one subject to the marks statement");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let saved;
      if (sid) {
        const { data } = await api.put(`/certificates/${sid}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/certificates", payload);
        saved = data;
      }
      toast.success(`${form.format === 2 ? "Marksheet" : "Certificate"} ${saved.certificate_number} saved`);
      if (openAfter) window.open(pdfUrl(saved.id), "_blank");
      nav("/admin/records");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (msMode) {
    return <MarksheetStudio sid={sid} preStudentId={searchParams.get("student")} />;
  }

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => nav(-1)}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
            {sid ? "Edit Document" : "New Certificate"}
          </h1>
          <p className="text-slate-500 mt-1">
            {form.format === 2
              ? "Marksheet with subject-wise marks, auto-calculated total and grade."
              : "Diploma certificate — subjects load automatically from the selected course."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="preview-btn" onClick={doPreview} className="h-10">
            <Eye size={16} className="mr-2" /> Preview
          </Button>
          <Button
            data-testid="generate-btn"
            onClick={() => doSave(true)}
            disabled={saving}
            className="h-10 bg-blue-800 hover:bg-blue-900"
          >
            <Download size={16} className="mr-2" />
            {saving ? "Saving…" : "Generate & Download"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* format selection */}
          {!sid && (
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <Label className="text-sm text-slate-700">Certificate format</Label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { v: 1, title: "Format 1 — Diploma / Certificate", desc: "Course completion certificate with grade.", testId: "format-1-option" },
                { v: 2, title: "Format 2 — Marksheet", desc: "Marks statement with subjects, totals & grade scale.", testId: "format-2-option" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={o.testId}
                  onClick={() => setFormat(o.v)}
                  className={`text-left rounded-lg border-2 p-4 transition-soft ${
                    form.format === o.v
                      ? "border-blue-700 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-4 h-4 rounded-full border-2 grid place-items-center ${
                        form.format === o.v ? "border-blue-700" : "border-slate-300"
                      }`}
                    >
                      {form.format === o.v && <span className="w-2 h-2 rounded-full bg-blue-700" />}
                    </span>
                    <span className="font-semibold text-slate-900 text-sm">{o.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 ml-6">{o.desc}</p>
                </button>
              ))}
            </div>
          </section>
          )}

          <Section title="Student information">
            <Row>
              <Field label="Student name *">
                <Input data-testid="f-name" value={form.student_name}
                  onChange={(e) => upd("student_name", e.target.value)} className="h-10"/>
              </Field>
              <Field label="Father's name">
                <Input data-testid="f-father" value={form.father_name}
                  onChange={(e) => upd("father_name", e.target.value)} className="h-10"/>
              </Field>
            </Row>
            <Row>
              <Field label="Mother's name">
                <Input data-testid="f-mother" value={form.mother_name}
                  onChange={(e) => upd("mother_name", e.target.value)} className="h-10"/>
              </Field>
              <Field label="Mobile number">
                <Input data-testid="f-mobile" value={form.mobile_number}
                  onChange={(e) => upd("mobile_number", e.target.value)} className="h-10"/>
              </Field>
            </Row>
            <Row>
              <Field label="Enrollment number">
                <Input data-testid="f-enrollment" value={form.enrollment_number}
                  onChange={(e) => upd("enrollment_number", e.target.value)} className="h-10"/>
              </Field>
              <Field label="Registration number">
                <Input data-testid="f-registration" value={form.registration_number}
                  onChange={(e) => upd("registration_number", e.target.value)} className="h-10"/>
              </Field>
            </Row>
            <Field label="Student photo (optional)">
              <ImageUpload testId="f-photo" value={form.photo}
                onChange={(v) => upd("photo", v)} hint="Small headshot works best" />
            </Field>
          </Section>

          <Section title="Course details">
            <Row>
              <Field label="Course *">
                <Select value={form.course_id} onValueChange={onCourseChange}>
                  <SelectTrigger data-testid="f-course" className="h-10">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.duration})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Course duration">
                <Input data-testid="f-duration" value={form.course_duration}
                  onChange={(e) => upd("course_duration", e.target.value)} className="h-10"/>
              </Field>
            </Row>
            <Row>
              <Field label="Session / course period">
                <Input data-testid="f-session" value={form.session}
                  onChange={(e) => upd("session", e.target.value)} className="h-10"
                  placeholder="e.g., July 2025 – June 2026"/>
              </Field>
              <Field label="Study centre / institute name">
                <Input data-testid="f-institute" value={form.institute_name}
                  onChange={(e) => upd("institute_name", e.target.value)} className="h-10"/>
              </Field>
            </Row>

            {form.format === 1 && selectedCourse && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <SubjectList title="Term 1" subjects={term1}
                  selectedIds={selectedSubjectIds} onToggle={toggleSubject} testId="term1" />
                <SubjectList title="Term 2" subjects={term2}
                  selectedIds={selectedSubjectIds} onToggle={toggleSubject} testId="term2" />
              </div>
            )}
          </Section>

          {form.format === 2 && (
            <Section title="Marks statement">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-2 font-medium">Subject</th>
                      <th className="py-2 px-2 font-medium w-24">Total</th>
                      <th className="py-2 px-2 font-medium w-24">Min. marks</th>
                      <th className="py-2 px-2 font-medium w-28">Marks obtained</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.marks.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-sm text-slate-400">
                          No subjects yet — select a course to auto-fill, or add rows manually.
                        </td>
                      </tr>
                    )}
                    {form.marks.map((m, i) => (
                      <tr key={m.id} className="border-b border-slate-100">
                        <td className="py-1.5 pr-2">
                          <Input data-testid={`marks-subject-${i}`} value={m.subject}
                            onChange={(e) => updMark(m.id, "subject", e.target.value)} className="h-9"/>
                        </td>
                        <td className="py-1.5 px-2">
                          <Input data-testid={`marks-total-${i}`} type="number" value={m.total}
                            onChange={(e) => updMark(m.id, "total", e.target.value)} className="h-9"/>
                        </td>
                        <td className="py-1.5 px-2">
                          <Input data-testid={`marks-min-${i}`} type="number" value={m.min}
                            onChange={(e) => updMark(m.id, "min", e.target.value)} className="h-9"/>
                        </td>
                        <td className="py-1.5 px-2">
                          <Input data-testid={`marks-obtained-${i}`} type="number" value={m.obtained}
                            onChange={(e) => updMark(m.id, "obtained", e.target.value)} className="h-9"/>
                        </td>
                        <td className="py-1.5 pl-2">
                          <button type="button" data-testid={`marks-del-${i}`} onClick={() => delMark(m.id)}
                            className="p-2 text-slate-400 hover:text-red-600 transition-soft">
                            <Trash2 size={15}/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="add-mark-row-btn" onClick={addMark}>
                  <Plus size={14} className="mr-1"/> Add subject
                </Button>
                <div data-testid="marks-summary" className="text-sm text-slate-700">
                  Grand total <b data-testid="marks-grand-total">{totals.t}</b> ·
                  Obtained <b data-testid="marks-obtained-total">{totals.o}</b> ·
                  Percentage <b data-testid="marks-percentage">{totals.pct}%</b> ·
                  Grade <b data-testid="marks-grade">{totals.grade}</b> ·
                  Result <b data-testid="marks-status" className={totals.status === "PASS" ? "text-green-700" : "text-red-600"}>{totals.status}</b>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Grade scale: {GRADE_SCALE_TEXT}. Result is FAIL if any subject is below its minimum marks.
              </p>
            </Section>
          )}

          <Section title="Certificate details">
            <Row>
              <Field label="Certificate number">
                <div className="flex gap-2">
                  <Input data-testid="f-certno" value={form.certificate_number}
                    onChange={(e) => upd("certificate_number", e.target.value)} className="h-10"/>
                  {!sid && (
                    <Button variant="outline" onClick={() => upd("certificate_number", nextNum)}
                      title="Reset to auto number" className="h-10 px-3">
                      <RefreshCw size={14} />
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Issue date">
                <Input data-testid="f-date" value={form.issue_date}
                  onChange={(e) => upd("issue_date", e.target.value)} className="h-10"/>
              </Field>
            </Row>
            <Row>
              {form.format === 1 && (
                <Field label="Grade (optional)">
                  <Input data-testid="f-grade" value={form.grade}
                    onChange={(e) => upd("grade", e.target.value)} className="h-10" placeholder="e.g., A+"/>
                </Field>
              )}
              {form.format === 1 && (
                <Field label="Template">
                  <Select value={form.template_id} onValueChange={(v) => upd("template_id", v)}>
                    <SelectTrigger data-testid="f-template" className="h-10">
                      <SelectValue placeholder="Default template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Admission number">
                <Input data-testid="f-admission" value={form.admission_number}
                  onChange={(e) => upd("admission_number", e.target.value)} className="h-10"/>
              </Field>
            </Row>
          </Section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-xl border border-slate-200 bg-white p-6 sticky top-6">
            <div className="text-xs uppercase tracking-wider text-blue-800 font-semibold">
              {form.format === 2 ? "Marksheet summary" : "Certificate summary"}
            </div>
            <div className="font-serif-display text-2xl text-slate-900 mt-2 leading-snug">
              {form.student_name || "Student name"}
            </div>
            <div className="text-sm text-slate-500">
              {form.father_name && `S/o ${form.father_name}`}
              {form.mother_name && ` · D/o ${form.mother_name}`}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <SummaryRow k="Format" v={form.format === 2 ? "Marksheet" : "Diploma / Certificate"} />
              <SummaryRow k="Course" v={form.course_name || "—"} />
              <SummaryRow k="Duration" v={form.course_duration || "—"} />
              {form.session && <SummaryRow k="Session" v={form.session} />}
              {form.format === 1 && <SummaryRow k="Subjects" v={`${form.subjects.length} selected`} />}
              {form.format === 2 && (
                <>
                  <SummaryRow k="Subjects" v={`${form.marks.length}`} />
                  <SummaryRow k="Total" v={`${totals.o} / ${totals.t}`} />
                  <SummaryRow k="Percentage" v={`${totals.pct}%`} />
                  <SummaryRow k="Grade" v={totals.grade} />
                  <SummaryRow k="Result" v={totals.status} />
                </>
              )}
              <SummaryRow k="Certificate #" v={form.certificate_number || "—"} />
              <SummaryRow k="Date" v={form.issue_date || "—"} />
              {form.format === 1 && form.grade && <SummaryRow k="Grade" v={form.grade} />}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 space-y-2">
              <Button
                onClick={() => doSave(false)}
                disabled={saving}
                variant="outline"
                data-testid="save-only-btn"
                className="w-full h-10"
              >
                <Save size={16} className="mr-2" /> Save without opening
              </Button>
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={preview.open} onOpenChange={(v) => setPreview({ open: v, url: preview.url })}>
        <DialogContent className="max-w-5xl w-[90vw] h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{form.format === 2 ? "Marksheet preview" : "Certificate preview"}</DialogTitle>
          </DialogHeader>
          <iframe src={preview.url} title="preview" className="w-full flex-1 rounded border border-slate-200"/>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-6">
    <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </section>
);
const Row = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);
const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-slate-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const SummaryRow = ({ k, v }) => (
  <div className="flex justify-between gap-2 text-slate-600">
    <span className="text-slate-500">{k}</span>
    <span className="font-medium text-slate-900 text-right truncate">{v}</span>
  </div>
);

function SubjectList({ title, subjects, selectedIds, onToggle, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-xs uppercase tracking-wider text-blue-800 font-semibold mb-2">{title}</div>
      <div className="space-y-2">
        {subjects.length === 0 && <div className="text-sm text-slate-400">No subjects</div>}
        {subjects.map((s) => (
          <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900 transition-soft">
            <Checkbox
              data-testid={`subj-${testId}-${s.id}`}
              checked={selectedIds.has(s.id)}
              onCheckedChange={() => onToggle(s)}
            />
            <span>{s.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
